import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { acAPI } from "../../lib/academicApi";
import { PageHeader, Loading, Section, StatusPill, EmptyState } from "../../components/Common";
import { ArrowLeft, Save, Plus, ArrowRight, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";

/**
 * MarkBooks index — lists assessments (filter by class/subject/term)
 * and navigates to an individual mark book grid.
 */
export function MarkBooksList() {
  const { school } = useParams();
  const { tenant } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  // Send the full canonical term ("First Term" / "Second Term" / "Third Term")
  // so it matches assessments written by the seeder. No cosmetic stripping.
  const [term, setTerm] = useState(() => tenant?.current_term || "Third Term");
  const academicYear = tenant?.academic_year || "2026";
  const [newForm, setNewForm] = useState({ title: "", type: "continuous", max_score: 100, weighting: 10, scheduled_at: "" });
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selClass, setSelClass] = useState("");
  const [selSubject, setSelSubject] = useState("");

  const load = () => {
    const p = {};
    if (selClass) p.class_id = selClass;
    if (selSubject) p.subject_id = selSubject;
    if (term) p.term = term;
    acAPI.assessments(p).then(({ data }) => setItems(data));
  };

  useEffect(() => {
    (async () => {
      const [c, s] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/education360/classes`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("edu360.token")}` }
        }).then(r => r.json()),
        acAPI.subjects().then(r => r.data),
      ]);
      setClasses(c || []); setSubjects(s || []);
    })();
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selClass, selSubject, term]);

  const create = async () => {
    if (!selClass || !selSubject || !newForm.title) return toast.error("Select class, subject and title");
    try {
      await acAPI.createAssessment({
        ...newForm,
        class_id: selClass,
        subject_id: selSubject,
        academic_year: academicYear,
        term,
      });
      toast.success("Assessment created");
      setNewForm({ title: "", type: "continuous", max_score: 100, weighting: 10, scheduled_at: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  if (items === null) return <Loading />;

  return (
    <div data-testid="markbooks-list">
      <PageHeader
        kicker="Academic360™ · Mark Books"
        title="Assessment grid"
        subtitle="Choose class + subject to enter or review marks."
        actions={<button onClick={() => nav(`/education360/${school}/academic`)} className="hl-button-secondary text-xs"><ArrowLeft className="w-3 h-3 inline mr-1" />Hub</button>}
      />

      <div className="hl-card p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-2 text-sm items-end" data-testid="filter-bar">
        <select value={selClass} onChange={e => setSelClass(e.target.value)} className="border border-slate-200 rounded px-2 py-2" data-testid="filter-class">
          <option value="">All classes</option>
          {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={selSubject} onChange={e => setSelSubject(e.target.value)} className="border border-slate-200 rounded px-2 py-2" data-testid="filter-subject">
          <option value="">All subjects</option>
          {subjects.map(s => <option key={s.id} value={s.id}>{s.name} ({s.year_group})</option>)}
        </select>
        <select value={term} onChange={e => setTerm(e.target.value)} className="border border-slate-200 rounded px-2 py-2" data-testid="filter-term">
          {(tenant?.terms || ["First Term", "Second Term", "Third Term"]).map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="text-xs text-slate-500 self-center">{items.length} assessments</div>
      </div>

      <Section title="New assessment">
        <div className="hl-card p-3 grid grid-cols-1 md:grid-cols-6 gap-2 text-sm items-end" data-testid="new-assessment-form">
          <input placeholder="Title" value={newForm.title} onChange={e => setNewForm({ ...newForm, title: e.target.value })} className="border border-slate-200 rounded px-2 py-2 md:col-span-2" data-testid="new-title" />
          <select value={newForm.type} onChange={e => setNewForm({ ...newForm, type: e.target.value })} className="border border-slate-200 rounded px-2 py-2" data-testid="new-type">
            {["class_exercise", "assignment", "project", "practical", "oral", "continuous", "test", "mid_term", "end_of_term", "mock", "cambridge", "national"].map(t => <option key={t}>{t}</option>)}
          </select>
          <input type="datetime-local" value={newForm.scheduled_at} onChange={e => setNewForm({ ...newForm, scheduled_at: e.target.value })} className="border border-slate-200 rounded px-2 py-2" data-testid="new-datetime" />
          <input type="number" placeholder="Max" value={newForm.max_score} onChange={e => setNewForm({ ...newForm, max_score: Number(e.target.value) })} className="border border-slate-200 rounded px-2 py-2" data-testid="new-max" />
          <button onClick={create} className="hl-button-primary text-xs" data-testid="btn-create-assessment"><Plus className="w-3 h-3 inline mr-1" />Create</button>
        </div>
      </Section>

      <div className="hl-card overflow-hidden">
        <table className="hl-table">
          <thead><tr><th>Title</th><th>Type</th><th>Class</th><th>Subject</th><th>Scheduled</th><th>Max</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-500">No assessments yet. Create one above.</td></tr>}
            {items.map(a => {
              const cls = classes.find(c => c.id === a.class_id);
              const subj = subjects.find(s => s.id === a.subject_id);
              return (
                <tr key={a.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/education360/${school}/academic/markbook/${a.id}`)}>
                  <td>{a.title}</td>
                  <td className="text-xs uppercase tracking-widest text-slate-500">{a.type}</td>
                  <td>{cls?.name || "—"}</td>
                  <td>{subj?.name || "—"}</td>
                  <td className="mono text-xs">{a.scheduled_at?.slice(0, 10)}</td>
                  <td>{a.max_score}</td>
                  <td><StatusPill status={a.status} /></td>
                  <td><ArrowRight className="w-4 h-4 text-slate-400" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Single assessment mark entry grid.
 */
export function MarkBookEntry() {
  const { school, id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState({});

  useEffect(() => {
    acAPI.markbook(id).then(({ data }) => {
      setData(data);
      const seed = {};
      (data.students || []).forEach(s => {
        const m = (data.marks || []).find(x => x.student_id === s.id);
        seed[s.id] = {
          raw_score: m?.raw_score ?? "",
          absent: !!m?.absent,
          remarks: m?.remarks || "",
        };
      });
      setRows(seed);
    });
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const marks = Object.entries(rows).map(([sid, v]) => ({
        student_id: sid, raw_score: v.raw_score, absent: v.absent, remarks: v.remarks,
      }));
      await acAPI.saveMarks(id, marks);
      toast.success("Marks saved · Gradebook rebuilt");
      // Reload markbook
      const { data } = await acAPI.markbook(id);
      setData(data);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const submitModeration = async () => {
    try {
      await acAPI.submitModeration({ assessment_id: id });
      toast.success("Submitted to HoD for moderation");
    } catch { toast.error("Failed"); }
  };

  if (!data) return <Loading />;
  if (!data.assessment) return <EmptyState title="Assessment not found" />;

  const a = data.assessment;

  return (
    <div data-testid={`markbook-${id}`}>
      <PageHeader
        kicker={`Academic360™ · Mark Book`}
        title={a.title}
        subtitle={`${a.type} · Max ${a.max_score} · ${a.term} ${a.academic_year}`}
        actions={
          <div className="flex gap-2">
            <button onClick={() => nav(`/education360/${school}/academic/markbooks`)} className="hl-button-secondary text-xs"><ArrowLeft className="w-3 h-3 inline mr-1" />Back</button>
            <button onClick={submitModeration} className="hl-button-secondary text-xs" data-testid="btn-submit-moderation"><ClipboardCheck className="w-3 h-3 inline mr-1" />Submit for moderation</button>
            <button onClick={save} disabled={saving} className="hl-button-primary text-xs disabled:opacity-50" data-testid="btn-save-marks"><Save className="w-3 h-3 inline mr-1" />{saving ? "Saving…" : "Save marks"}</button>
          </div>
        }
      />

      <div className="hl-card overflow-hidden">
        <table className="hl-table">
          <thead><tr>
            <th>#</th><th>Student</th><th>Score</th><th>%</th><th>Grade</th><th>Absent</th><th>Remarks</th>
          </tr></thead>
          <tbody>
            {(data.students || []).map((s, i) => {
              const row = rows[s.id] || {};
              const pct = row.raw_score !== "" && row.raw_score != null ? ((Number(row.raw_score) / a.max_score) * 100).toFixed(1) : "";
              return (
                <tr key={s.id}>
                  <td className="text-xs text-slate-400 mono">{i + 1}</td>
                  <td><b>{s.first_name} {s.last_name}</b><div className="text-xs text-slate-500">{s.year_group}</div></td>
                  <td>
                    <input
                      type="number" min={0} max={a.max_score}
                      value={row.raw_score}
                      disabled={row.absent}
                      onChange={e => setRows({ ...rows, [s.id]: { ...row, raw_score: e.target.value } })}
                      className="border border-slate-200 rounded px-2 py-1 w-20 text-sm"
                      data-testid={`mark-${s.id}`}
                    />
                    <span className="text-xs text-slate-400 ml-1">/ {a.max_score}</span>
                  </td>
                  <td className="mono text-xs">{pct !== "" ? `${pct}%` : "—"}</td>
                  <td className="text-xs">
                    {row.raw_score !== "" && row.raw_score != null && !row.absent ? gradeFromScore(Number(pct)) : "—"}
                  </td>
                  <td>
                    <input type="checkbox" checked={!!row.absent} onChange={e => setRows({ ...rows, [s.id]: { ...row, absent: e.target.checked } })} data-testid={`abs-${s.id}`} />
                  </td>
                  <td>
                    <input
                      value={row.remarks || ""}
                      onChange={e => setRows({ ...rows, [s.id]: { ...row, remarks: e.target.value } })}
                      className="border border-slate-200 rounded px-2 py-1 w-full text-xs"
                      placeholder="Optional comment"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function gradeFromScore(pct) {
  if (pct >= 90) return "A*"; if (pct >= 75) return "A"; if (pct >= 65) return "B";
  if (pct >= 55) return "C"; if (pct >= 50) return "D"; if (pct >= 35) return "E"; return "U";
}
